// components/About.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Heart,
  Target,
  Award,
  Users,
  Dumbbell,
  Apple,
  Brain,
  Activity,
  Shield,
  AlertCircle,
  Loader2,
  Sparkles,
  CheckCircle,
  Star,
  Zap,
} from "lucide-react";
import { usePageData } from "../../../hooks/useProfilePageData";

const About = () => {
  const { pageData, loading, error } = usePageData("about-us");
  const navigate = useNavigate();

  const parseSections = (description) => {
    if (!description) return [];

    const sections = [];
    const lines = description.split("\n");
    let currentSection = null;
    let currentContent = [];

    for (const line of lines) {
      const trimmedLine = line.trim();
      if (trimmedLine.endsWith(":") && !trimmedLine.startsWith("•")) {
        if (currentSection) {
          sections.push({
            title: currentSection,
            content: currentContent.join("\n"),
          });
        }
        currentSection = trimmedLine.slice(0, -1);
        currentContent = [];
      } else if (trimmedLine && currentSection) {
        currentContent.push(trimmedLine);
      } else if (trimmedLine && !currentSection) {
        sections.push({ title: null, content: trimmedLine });
      }
    }

    if (currentSection) {
      sections.push({
        title: currentSection,
        content: currentContent.join("\n"),
      });
    }

    return sections;
  };

  const getCardStyle = (index) => {
    const styles = [
      { 
        bg: "from-blue-50 to-blue-100/50", 
        icon: "text-blue-600", 
        border: "border-blue-200",
        badge: "bg-blue-100 text-blue-700",
      },
      { 
        bg: "from-green-50 to-green-100/50", 
        icon: "text-green-600", 
        border: "border-green-200",
        badge: "bg-green-100 text-green-700",
      },
      { 
        bg: "from-amber-50 to-amber-100/50", 
        icon: "text-amber-600", 
        border: "border-amber-200",
        badge: "bg-amber-100 text-amber-700",
      },
      { 
        bg: "from-purple-50 to-purple-100/50", 
        icon: "text-purple-600", 
        border: "border-purple-200",
        badge: "bg-purple-100 text-purple-700",
      },
      { 
        bg: "from-rose-50 to-rose-100/50", 
        icon: "text-rose-600", 
        border: "border-rose-200",
        badge: "bg-rose-100 text-rose-700",
      },
      { 
        bg: "from-teal-50 to-teal-100/50", 
        icon: "text-teal-600", 
        border: "border-teal-200",
        badge: "bg-teal-100 text-teal-700",
      },
      { 
        bg: "from-indigo-50 to-indigo-100/50", 
        icon: "text-indigo-600", 
        border: "border-indigo-200",
        badge: "bg-indigo-100 text-indigo-700",
      },
      { 
        bg: "from-orange-50 to-orange-100/50", 
        icon: "text-orange-600", 
        border: "border-orange-200",
        badge: "bg-orange-100 text-orange-700",
      },
    ];
    return styles[index % styles.length];
  };

  const getIconForSection = (title, className = "w-5 h-5") => {
    if (!title) return <Heart className={`${className} text-red-500`} />;
    const t = title.toLowerCase();
    if (t.includes("who we are")) return <Users className={`${className}`} />;
    if (t.includes("what we offer")) return <Dumbbell className={`${className}`} />;
    if (t.includes("why choose")) return <Award className={`${className}`} />;
    if (t.includes("mission")) return <Target className={`${className}`} />;
    if (t.includes("live")) return <Activity className={`${className}`} />;
    if (t.includes("diet")) return <Apple className={`${className}`} />;
    if (t.includes("mindset")) return <Brain className={`${className}`} />;
    if (t.includes("physiotherapy")) return <Shield className={`${className}`} />;
    return <Heart className={`${className}`} />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-gray-200 rounded-full animate-spin mx-auto"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <Heart className="w-6 h-6 text-lime-500 animate-pulse" />
            </div>
          </div>
          <p className="mt-6 text-gray-600 text-lg font-medium">Loading amazing content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center p-4">
        <div className="text-center bg-white rounded-3xl shadow-xl p-8 max-w-md transition-transform duration-300 hover:scale-105">
          <div className="bg-red-100 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-12 h-12 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Oops! Something went wrong</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-3">
            <button
              onClick={() => window.location.reload()}
              className="w-full px-6 py-3 bg-lime-500 text-white rounded-xl hover:bg-lime-600 transition-all transform hover:scale-105 shadow-md"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate(-1)}
              className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!pageData) return null;

  const sections = parseSections(pageData.description);
  const introSections = sections.filter((s) => !s.title);
  const cardSections = sections.filter((s) => s.title);

  return (
    <div className="bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Decorative Elements */}
      <div className="fixed top-0 right-0 w-64 h-64 bg-gradient-to-br from-lime-100/30 to-green-100/30 rounded-full blur-3xl -z-10"></div>
      <div className="fixed bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-blue-100/20 to-purple-100/20 rounded-full blur-3xl -z-10"></div>
      
     <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center justify-between ">
            <button
              onClick={() => navigate(-1)}
              className="group inline-flex items-center gap-2 px-4 py-2.5 text-gray-700 bg-white rounded-xl hover:bg-gray-50 transition-all shadow-sm hover:shadow-md border border-gray-200"
            >
              <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
              <span className="text-sm font-medium">Back</span>
            </button>
          </div>
          </div>

        {/* Hero Intro Section */}
        {introSections.length > 0 && (
          <div className="relative mb-12">
            <div className="absolute inset-0 bg-gradient-to-r from-lime-500/5 to-green-500/5 rounded-3xl"></div>
            <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-gray-100 p-8 md:p-10">
              <div className="flex flex-col md:flex-row gap-8 items-start">
              
                <div className="flex-1 space-y-4">
                  {introSections.map((s, i) => (
                    <p key={i} className="text-gray-700 leading-relaxed text-base">
                      {s.content}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Section Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {cardSections.map((section, idx) => {
            const style = getCardStyle(idx);
            return (
              <div
                key={idx}
                className="group transition-all duration-300 hover:-translate-y-2"
              >
                <div className={`relative bg-white rounded-2xl shadow-lg border ${style.border} overflow-hidden transition-all duration-300 h-full hover:shadow-xl`}>
                  {/* Card Header */}
                  <div className={`bg-gradient-to-r ${style.bg} px-6 py-5 border-b ${style.border} relative overflow-hidden`}>
                    <div className="absolute top-0 right-0 w-20 h-20 opacity-10">
                      <Star className="w-full h-full" />
                    </div>
                    <div className="flex items-center gap-3 relative z-10">
                      <div className={`p-2 rounded-xl bg-white shadow-sm ${style.icon}`}>
                        {getIconForSection(section.title, "w-5 h-5")}
                      </div>
                      <div className="flex-1">
                        <h2 className="text-lg font-bold text-gray-800">
                          {section.title}
                        </h2>
                        <div className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full mt-1 ${style.badge}`}>
                          Feature
                        </div>
                      </div>
                      <Zap className="w-4 h-4 text-gray-400 transition-transform group-hover:rotate-12" />
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="px-6 py-5">
                    <p className="text-gray-600 leading-relaxed whitespace-pre-line text-sm">
                      {section.content}
                    </p>
                    
                    {/* Decorative Checkmark */}
                    <div className="mt-4 flex justify-end">
                      <CheckCircle className="w-4 h-4 text-green-500 opacity-0 transition-opacity group-hover:opacity-100" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

       
      </div>
    </div>
  );
};

export default About;